import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Collapse,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

/**
 * Collapsible panel showing only the citations that were actually referenced
 * in the response text ([1], [2], etc.).
 *
 * Props:
 *   citations: Array<{id, content, score, source, filename, type, source_kb}>
 *   referencedIndices: Set<number>  — 0-based indices of citations used in the text
 *
 * Future: add a "View Document" button using citation.source (S3 URI) once a
 * pre-signed URL endpoint or document proxy is available.
 */
/** Truncate long filenames / URLs to a reasonable display length. */
const truncateFilename = (name, maxLen = 60) => {
  if (!name || name.length <= maxLen) return name;
  return name.slice(0, maxLen - 1) + '…';
};

function CitationDisplay({ citations, referencedIndices }) {
  const [expanded, setExpanded] = useState(false);
  const [expandedExcerpts, setExpandedExcerpts] = useState({});

  if (!citations || citations.length === 0) return null;

  // Filter to only the citations actually referenced in the response
  const referencedCitations = citations
    .map((c, i) => ({ ...c, originalIndex: i }))
    .filter((c) => !referencedIndices || referencedIndices.has(c.originalIndex));

  if (referencedCitations.length === 0) return null;

  const toggleExcerpt = (key) => {
    setExpandedExcerpts((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getKbLabel = (source_kb) => {
    if (source_kb === 'budget_process') return 'Budget Process';
    if (source_kb === 'congressional_justification') return 'Congressional Justification';
    if (source_kb === 'va_budget_policy') return 'VA Budget Policy';
    return null;
  };

  const EXCERPT_LIMIT = 300;

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {/* Header / toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1,
          cursor: 'pointer',
          backgroundColor: 'grey.50',
          '&:hover': { backgroundColor: 'grey.100' },
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <MenuBookIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', flexGrow: 1 }}>
          Referenced Sources ({referencedCitations.length})
        </Typography>
        <IconButton size="small" disableRipple>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Citation cards */}
      <Collapse in={expanded} timeout="auto">
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {referencedCitations.map((citation, listIndex) => {
            const displayNumber = citation.originalIndex + 1; // matches [N] in the text
            const excerptFull = citation.content || '';
            const isLong = excerptFull.length > EXCERPT_LIMIT;
            const key = citation.id || citation.originalIndex;
            const showFull = expandedExcerpts[key];
            const excerptDisplay = isLong && !showFull
              ? excerptFull.slice(0, EXCERPT_LIMIT) + '…'
              : excerptFull;
            const kbLabel = getKbLabel(citation.source_kb);

            return (
              <React.Fragment key={key}>
                {listIndex > 0 && <Divider />}
                <Box sx={{ pt: listIndex > 0 ? 0.5 : 0 }}>
                  {/* Filename + KB label */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      [{displayNumber}] {truncateFilename(citation.filename) || 'Unknown Source'}
                    </Typography>
                    {kbLabel && (
                      <Chip
                        label={kbLabel}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 20, color: 'text.secondary' }}
                      />
                    )}
                  </Box>

                  {/* Excerpt */}
                  <Box
                    sx={{
                      pl: 1.5,
                      borderLeft: '3px solid',
                      borderColor: 'grey.300',
                      mt: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'block' }}
                    >
                      {excerptDisplay}
                    </Typography>
                    {isLong && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'primary.main', cursor: 'pointer', mt: 0.25, display: 'inline-block' }}
                        onClick={() => toggleExcerpt(key)}
                      >
                        {showFull ? 'Show less' : 'Show more'}
                      </Typography>
                    )}
                  </Box>

                  {/* Link to source document if URL is available */}
                  {citation.url && (
                    <Typography
                      variant="caption"
                      component="a"
                      href={citation.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-block',
                        mt: 0.75,
                        ml: 1.5,
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      View Source
                    </Typography>
                  )}
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      </Collapse>
    </Paper>
  );
}

export default CitationDisplay;
